import { buildYesterday, isSameDay, isValidDate } from '@tuhi/components'
import { v4 as uuidv4 } from 'uuid'
import { ConsultState, mapToConsultState } from 'app/models/domain/consult'
import {
    Accuracy,
    AttachmentDto,
    FormDto,
    ConsultDto,
    ConsultTranscriptionDto,
    DiarizationDto,
    EntityDto,
    PatientDto,
    TemplateDto,
} from 'app/models/generated'
import { getName } from 'app/features/patient/helpers/patientDtoMapper'
import {
    ConsultType,
    consultTypeDisplayValue,
    mapToConsultType,
} from 'app/models/domain/consult/consultType'
import { logger } from 'app/logging/Logger'

export class Consult {
    id!: string
    owner!: string
    authId!: string
    patientId?: string | null
    title?: string | null
    templateType!: string
    catalogueTemplateId?: string | null
    catalogueTemplateVersion?: number | null
    type?: ConsultType
    startDate!: Date
    completionDate!: Date
    summary?: string | null
    tags: string[] = []
    state?: ConsultState
    attachments?: AttachmentDto[] | null
    entities?: EntityDto[] | null
    template?: TemplateDto | null
    webContent?: string | null
    additionalTemplates?: TemplateDto[] | null
    matchedPatients?: PatientDto[] | null
    audioId?: string | null
    rating?: number | null
    attachedPatient?: PatientDto | null
    accuracy?: string | null
    forms!: FormDto[] | null

    // Public optional properties for enrichment layer to set
    diarization?: DiarizationDto[] | null
    transcription?: ConsultTranscriptionDto | null
    embeddedCloudTranscription?: string | null

    static buildFromDto(dto: ConsultDto): Consult {
        const consult = new Consult()
        consult.id = dto.id
        consult.authId = dto.authId
        consult.title = dto.title
        consult.type = mapToConsultType(dto.type)
        consult.templateType = dto.type
        consult.catalogueTemplateId = dto.catalogueTemplateId ?? null
        consult.catalogueTemplateVersion = dto.catalogueTemplateVersion ?? null
        consult.startDate = mapToDate(dto.startDate)
        consult.completionDate = mapToDate(dto.completionDate)
        consult.summary = dto.summary
        consult.tags = (dto.tags ?? []).filter(
            (t: unknown): t is string => typeof t === 'string' && t.length > 0
        )
        consult.state = mapToConsultState(dto.state)
        consult.attachments = dto.attachments

        // Backward compatibility: Load entities from embedded field if it exists (old consults)
        consult.entities = dto.entities ?? []

        // Backward compatibility: store embedded diarization/cloudTranscription if they exist
        // Old consults embedded these fields, which are no longer on ConsultDto
        const legacy = dto as ConsultDto & {
            diarization?: DiarizationDto[] | null
            cloudTranscription?: string | null
        }
        if (legacy.diarization && legacy.diarization.length > 0) {
            consult.diarization = legacy.diarization
            logger.debug('Loaded embedded diarization from DTO', {
                action: 'build_from_dto_embedded_diarization',
                metadata: {
                    consultId: dto.id,
                    segmentCount: legacy.diarization.length,
                },
            })
        }
        if (legacy.cloudTranscription) {
            consult.embeddedCloudTranscription = legacy.cloudTranscription
            logger.debug('Loaded embedded cloudTranscription from DTO', {
                action: 'build_from_dto_embedded_cloud_transcription',
                metadata: { consultId: dto.id },
            })
        }

        // Backward compatibility: keep embedded values as fallback during dual-write phase
        consult.matchedPatients =
            dto.matchedPatients?.filter(
                (patient): patient is PatientDto => patient != null
            ) ?? null
        consult.audioId = dto.audioId
        consult.rating = dto.rating
        consult.template = dto.template
        consult.webContent = dto.template?.webContent
        consult.attachedPatient = dto.attachedPatient ?? null
        consult.patientId = dto.patientId ?? null
        consult.additionalTemplates = dto.additionalTemplates
        consult.accuracy = dto.accuracy
        return consult
    }

    static buildFromTranscription(
        authId: string,
        sub: string,
        type: string,
        startDate: Date,
        clientTranscription: string,
        accuracy: Accuracy,
        diarization: DiarizationDto[],
        entities: EntityDto[],
        patient?: PatientDto,
        catalogueTemplateId?: string | null,
        catalogueTemplateVersion?: number | null
    ) {
        const consult = new Consult()
        consult.id = uuidv4()
        consult.owner = sub
        consult.authId = authId
        consult.patientId = patient?.id
        consult.title = patient && `${patient.id} | ${getName(patient)}`
        consult.attachedPatient = patient ?? null
        consult.type = mapToConsultType(type)
        consult.templateType = type
        consult.catalogueTemplateId = catalogueTemplateId ?? null
        consult.catalogueTemplateVersion = catalogueTemplateVersion ?? null
        consult.startDate = startDate
        consult.completionDate = new Date()
        consult.summary = clientTranscription.substring(0, 500)
        consult.state = ConsultState.Default
        consult.attachments = []
        consult.entities = entities

        // Store transcription temporarily for creation (will be saved to separate table)
        consult.transcription = {
            clientTranscription,
            cloudTranscription: null,
        } as ConsultTranscriptionDto

        // Store diarization temporarily for creation
        consult.diarization = diarization

        consult.template = null
        consult.matchedPatients = null
        consult.rating = 0
        consult.accuracy = accuracy

        return consult
    }

    /**
     * Builds a blank consult with no transcription/audio.
     *
     * Used by the "Add consultation" flow, which immediately creates an empty
     * consult without recording. Mandatory schema fields are populated with
     * blank strings / empty arrays so the createConsultDto mutation passes
     * validation. The consult is created in a terminal state so the History
     * list renders it as a normal (openable) row rather than a perpetual
     * "Starting..." spinner (History uses Consult.isComplete() to gate this).
     */
    static buildBlank(authId: string, sub: string, type: string): Consult {
        const consult = new Consult()
        const now = new Date()
        consult.id = uuidv4()
        consult.owner = sub
        consult.authId = authId
        consult.patientId = null
        consult.title = ''
        consult.attachedPatient = null
        consult.type = mapToConsultType(type)
        consult.templateType = type
        consult.catalogueTemplateId = null
        consult.catalogueTemplateVersion = null
        consult.startDate = now
        consult.completionDate = now
        consult.summary = ''
        consult.state = ConsultState.TemplateComplete
        consult.attachments = []
        consult.entities = []
        consult.diarization = null
        consult.transcription = null
        consult.template = null
        consult.matchedPatients = null
        consult.rating = 0
        consult.accuracy = Accuracy.DEFAULT
        return consult
    }

    static buildConsultDto(
        consult: Consult,
        audioId: string | null
    ): ConsultDto {
        return new ConsultDto({
            authId: consult.authId,
            owner: consult.owner,
            patientId: consult.patientId,
            title: consult.title,
            type: consult.templateType,
            catalogueTemplateId: consult.catalogueTemplateId ?? null,
            catalogueTemplateVersion: consult.catalogueTemplateVersion ?? null,
            startDate: consult.startDate.toISOString(),
            completionDate: consult.completionDate.toISOString(),
            summary: consult.summary,
            // Only new consults reach here, and buildFromTranscription and
            // buildBlank both set a state, so this fallback is never used in
            // practice; Default is the state a new consult starts in.
            state: consult.state ?? ConsultState.Default,
            attachments: consult.attachments,
            entities: consult.entities,
            template: consult.template,
            matchedPatients: consult.matchedPatients,
            audioId: audioId,
            rating: consult.rating,
            accuracy: consult.accuracy == Accuracy.DEFAULT ? 'DEFAULT' : 'HIGH',
        })
    }

    static buildConsultDtoForState(
        consult: Consult,
        state: ConsultState
    ): ConsultDto {
        return new ConsultDto({
            authId: consult.authId,
            owner: consult.owner,
            patientId: consult.patientId,
            title: consult.title,
            type: consult.templateType,
            catalogueTemplateId: consult.catalogueTemplateId ?? null,
            catalogueTemplateVersion: consult.catalogueTemplateVersion ?? null,
            startDate: consult.startDate.toISOString(),
            completionDate: consult.completionDate.toISOString(),
            summary: consult.summary,
            state: state,
            attachments: consult.attachments,
            entities: consult.entities,
            template: consult.template,
            matchedPatients: consult.matchedPatients,
            rating: consult.rating,
            accuracy: consult.accuracy == Accuracy.DEFAULT ? 'DEFAULT' : 'HIGH',
        })
    }

    isComplete(): boolean {
        const state = this.state
        return (
            (state !== undefined &&
                [
                    ConsultState.TemplateComplete,
                    ConsultState.TemplateFailed,
                    ConsultState.Retry,
                    ConsultState.NewTemplateComplete,
                    ConsultState.PatientFound,
                    ConsultState.PatientNotFound,
                    ConsultState.PatientMatched,
                    ConsultState.FormComplete,
                    ConsultState.FormPending,
                    ConsultState.FormInProgress,
                    ConsultState.FormFailed,
                ].includes(state)) ||
            this.template != undefined
        )
    }

    needsPatientAttachment(): boolean {
        return (
            this.patientId == null &&
            this.attachedPatient == null &&
            this.isComplete()
        )
    }

    getDateTitle(): string {
        const today = new Date()
        const yesterday = buildYesterday(today)

        const options: Intl.DateTimeFormatOptions = {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric',
        }

        if (isSameDay(this.startDate, today)) {
            return 'Today'
        } else if (isSameDay(this.startDate, yesterday)) {
            return 'Yesterday'
        } else if (!isValidDate(this.startDate)) {
            // ACA-177: an unparseable startDate must not render the literal
            // "Invalid Date" as a group heading in the clinician's history.
            return 'Unknown date'
        } else {
            const formattedDate = this.startDate.toLocaleDateString(
                'en-GB',
                options
            )
            return formattedDate.replace(/(\w+)(\s)(\d+)/, '$1,$2$3')
        }
    }

    getTimeframe(): string {
        const options: Intl.DateTimeFormatOptions = {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
        }

        const startTime = this.startDate
            .toLocaleTimeString('en-GB', options)
            .toLowerCase()
        const endTime = this.completionDate
            .toLocaleTimeString('en-GB', options)
            .toLowerCase()

        return `${startTime} - ${endTime}`
    }

    /**
     * Returns true when this consult uses the fast synchronous pipeline
     * (DEFAULT accuracy — Chat Completions completes in ~2-5s).
     */
    isFastPipeline(): boolean {
        return this.accuracy === 'DEFAULT'
    }

    getProgress(): number {
        // DEFAULT accuracy uses a compressed progress scale because the
        // backend completes in ~2-5s via Chat Completions (no transcription,
        // no diarization, no entity extraction — straight to template).
        // However, HD upgrade flow keeps accuracy=DEFAULT while going through
        // the full HIGH pipeline, so we include transcription/diarization/entity
        // states here too.
        if (this.isFastPipeline()) {
            switch (this.state) {
                case ConsultState.Default:
                    return 0.15
                case ConsultState.AudioUploaded:
                case ConsultState.ProcessingAudio:
                    return 0.4
                case ConsultState.TranscriptionInProgress:
                    return 0.3
                case ConsultState.TranscriptionComplete:
                    return 0.5
                case ConsultState.DiarizationInProgress:
                case ConsultState.EntitiesInProgress:
                    return 0.55
                case ConsultState.DiarizationComplete:
                case ConsultState.EntitiesComplete:
                    return 0.6
                case ConsultState.TemplateInProgress:
                case ConsultState.NewTemplateInProgress:
                    return 0.7
                case ConsultState.TemplateComplete:
                case ConsultState.NewTemplateComplete:
                    return 0.9
                case ConsultState.FormPending:
                    return 0.65
                case ConsultState.FormInProgress:
                    return 0.75
                case ConsultState.Retry:
                    return 0.5
                default:
                    return 0
            }
        }

        // HIGH accuracy — full pipeline with transcription, entities,
        // diarization, and template generation (~30-120s).
        switch (this.state) {
            case ConsultState.Default:
                return 0.1
            case ConsultState.AudioUploaded:
            case ConsultState.ProcessingAudio:
                return 0.2
            case ConsultState.TranscriptionInProgress:
                return 0.3
            case ConsultState.TranscriptionComplete:
                return 0.5
            case ConsultState.DiarizationInProgress:
            case ConsultState.EntitiesInProgress:
                return 0.55
            case ConsultState.DiarizationComplete:
            case ConsultState.EntitiesComplete:
                return 0.6
            case ConsultState.TemplateInProgress:
            case ConsultState.NewTemplateInProgress:
                return 0.7
            case ConsultState.TemplateComplete:
            case ConsultState.NewTemplateComplete:
                return 0.9
            case ConsultState.FormPending:
                return 0.65
            case ConsultState.FormInProgress:
                return 0.75
            case ConsultState.Retry:
                return 0.5
            default:
                return 0
        }
    }

    getEntities(): string[] {
        const arr = (this.entities ?? []).filter(Boolean)
        const texts = arr
            .map((entity) => entity?.entityText?.toLowerCase?.() ?? '')
            .filter((t) => t && t.length > 0)
        return Array.from(new Set(texts)).sort((a, b) =>
            a.toLowerCase().localeCompare(b.toLowerCase())
        )
    }

    /**
     * AI-generated consultation tags (ACA-120). Normalised: trimmed,
     * de-duplicated case-insensitively, and sorted. Distinct from
     * getEntities() (NER-extracted entity text).
     */
    getTags(): string[] {
        const arr = (this.tags ?? []).filter(
            (t): t is string => typeof t === 'string' && t.trim().length > 0
        )
        const seen = new Set<string>()
        const result: string[] = []
        for (const raw of arr) {
            const t = raw.trim()
            const key = t.toLowerCase()
            if (seen.has(key)) continue
            seen.add(key)
            result.push(t)
        }
        return result.sort((a, b) =>
            a.toLowerCase().localeCompare(b.toLowerCase())
        )
    }

    getTypeDisplayValue(): string {
        if (this.type) {
            return consultTypeDisplayValue(this.type)
        }
        // Fallback for types not in the enum (e.g. Pathology Report)
        // Convert SCREAMING_SNAKE_CASE to Title Case
        return (
            this.templateType
                ?.replace(/_/g, ' ')
                ?.replace(
                    /\w\S*/g,
                    (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()
                ) ?? 'Unknown Type'
        )
    }
}

function mapToDate(value: string): Date {
    return new Date(value)
}
